import { CircleAlert } from "lucide-react"

import { formatDateTimeInTimeZone } from "../../lib/format-date"
import {
  formatRunDuration,
  runStatusLabel,
  scheduleDuration,
  scheduleRelative,
} from "../../lib/scheduled-job-display"
import type { ScheduledJob } from "../../services/use-scheduled-jobs"
import { ScheduleRunNow } from "./schedule-run-now"

export function ScheduleRunHealth({ job }: { job: ScheduledJob }) {
  const next = job.enabled && !job.manualOnly ? job.nextRunAt : null
  const last = job.runSummary.last
  const failure = job.runSummary.latestFailure
  const noNext = job.manualOnly ? "Manual only" : "Paused"
  const noNextNote = job.manualOnly ? "Use Run now to start" : "Resume to schedule runs"
  const zone = job.schedule.timeZone
  return (
    <>
      <section className="card stats" aria-label="Job overview">
        <div className="od-stat">
          <span className="stat-label">Next run</span>

          <span className="stat-value">{next ? scheduleRelative(next, zone) : noNext}</span>

          <span className="stat-note">
            {next ? formatDateTimeInTimeZone(next, zone) : noNextNote}
          </span>
        </div>

        <div className="od-stat">
          <span className="stat-label">Last run</span>

          <span className={failure ? "stat-value c-destructive" : "stat-value"}>
            {last ? runStatusLabel(last) : "—"}
          </span>

          <span className="stat-note">
            {last
              ? `${scheduleRelative(last.triggeredAt, zone)} · ${formatRunDuration(last) ?? "—"}`
              : "No runs yet"}
          </span>
        </div>

        <div className="od-stat">
          <span className="stat-label">Success rate</span>

          <span className="stat-value">
            {job.stats.finished
              ? `${Math.round((job.stats.succeeded / job.stats.finished) * 100)}%`
              : "—"}
          </span>

          <span className="stat-note">
            {`${job.stats.succeeded} of ${job.stats.finished} recent runs`}
          </span>
        </div>

        <div className="od-stat">
          <span className="stat-label">Avg duration</span>

          <span className="stat-value">{scheduleDuration(job.stats.avgDurationMs)}</span>

          <span className="stat-note">Last {job.stats.finished} runs</span>
        </div>
      </section>

      {failure ? (
        <div className="banner" role="alert">
          <CircleAlert className="icon" />

          <div className="od-fill">
            <div className="banner-title">
              {`Last run ${failure.effectiveStatus} · ${formatDateTimeInTimeZone(failure.triggeredAt, zone)}`}
            </div>

            <p className="break-words">
              {failure.failureSummary ?? "The run did not finish successfully."}
            </p>
          </div>

          <ScheduleRunNow jobId={job.id} retry />
        </div>
      ) : null}
    </>
  )
}
